import {
  ChatService,
  ConversationService,
  ConversationStatus,
  ConversationType,
  TChatChannel,
} from '@talon/web/lib/api';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { RootStore } from '.';
import { BaseStore } from './baseStore';

/**
 * The user's chat channels and their unread counts (for the chat widget and badges elsewhere), plus
 * the cross-component signal for "open the chat widget on this conversation" — driven by notification
 * clicks, the `?openChat=` deep-link query param, and Message buttons (see ChatWidget).
 */
export class ChatStore extends BaseStore {
  pendingChannelId: string | null = null;
  channels: TChatChannel[] = [];
  /** Support agents and admins: unread messages across the support inbox */
  inboxUnread = 0;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      pendingChannelId: observable,
      channels: observable,
      inboxUnread: observable,
      totalUnread: computed,
      supportChannel: computed,
      markRead: action,
    });
  }

  get totalUnread(): number {
    return this.channels.reduce((sum, c) => sum + c.unreadCount, 0);
  }

  /** The user's own support conversation, once they've asked for one */
  get supportChannel(): TChatChannel | undefined {
    return this.channels.find((c) => c.conversationType === ConversationType.SUPPORT);
  }

  /** Unread messages in the conversation between an agency and the talent manager it engaged */
  unreadForEngagement = (engagementId: string): number =>
    this.channels.find((c) => c.engagementId === engagementId)?.unreadCount ?? 0;

  fetchChannels = async (): Promise<TChatChannel[]> => {
    try {
      const { channels } = await ChatService.listMyChannels();
      runInAction(() => {
        this.channels = channels;
      });
      return channels;
    } catch (e) {
      console.log('error fetching channels', e);
      return this.channels;
    }
  };

  /** Support agents and admins: how much of the support inbox they haven't read */
  fetchInboxUnread = async (): Promise<void> => {
    try {
      const { conversations } = await ConversationService.listConversations(ConversationStatus.OPEN, 100);
      runInAction(() => {
        this.inboxUnread = conversations.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
      });
    } catch (e) {
      console.log('error fetching inbox unread', e);
    }
  };

  /** The user has read a channel (the server is told separately) */
  markRead = (conversationId: string) => {
    this.channels = this.channels.map((c) => (c.conversationId === conversationId ? { ...c, unreadCount: 0 } : c));
  };

  openChannel = (conversationId: string) => {
    runInAction(() => {
      this.pendingChannelId = conversationId;
    });
  };

  /**
   * Opens the conversation between an agency and the talent manager it engaged, from either side.
   * Engagements don't carry their conversation, but each chat channel names its engagement.
   * Returns false when there's no such conversation.
   */
  openEngagementChannel = async (engagementId: string): Promise<boolean> => {
    const channel = (await this.fetchChannels()).find((c) => c.engagementId === engagementId);
    if (!channel) return false;
    this.openChannel(channel.conversationId);
    return true;
  };

  /** Opens the user's own support conversation, starting it if this is the first time they've asked */
  openSupportChannel = async (): Promise<boolean> => {
    try {
      const { conversation } = await this.q(ChatService.openSupportConversation());
      await this.fetchChannels();
      this.openChannel(conversation.id);
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  };

  clearPendingChannel = () => {
    runInAction(() => {
      this.pendingChannelId = null;
    });
  };
}
